import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, TextField, FormControlLabel, Checkbox } from '@material-ui/core';
import { Alert } from '@mui/material';
import { useHistory } from 'react-router-dom';

import {
  NonprofitIdentifierType,
  RoleCode,
  useCreateChapterRequestMutation,
  useUpdateChapterRequestMutation,
} from 'generated/graphql';
import Modal, { ModalTitle, ModalContents } from 'components/Modal/Modal';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { DeedHeartHands } from 'components/Flow/components/DeedHeartHands';
import { isEmail, isLocationObject } from 'utils/validators';
import { FormLocationObjectField } from 'retired/FormLocationObjectField/FormLocationObjectField';
import LocationObject from 'entities/locationObject/model';

interface ChapterRequestModalProps {
  modalOpen: boolean;
  chapterRequest?: Record<string, any> | null;
  onClose: () => void;
}

interface ChapterFormData {
  parentName: string;
  name: string;
  mailingAddress: LocationObject;
  emailAddress: string;
  ein: string;
  addAdditionalAdmin: boolean;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
}

const ChapterRequestModal = ({
  chapterRequest = null,
  modalOpen,
  onClose,
}: ChapterRequestModalProps): ReactElement | null => {
  const history = useHistory();
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  const [createChapterRequestMutation, { loading: creating, error: creatingError }] =
    useCreateChapterRequestMutation();
  const [updateChapterRequestMutation, { loading: updating, error: updatingError }] =
    useUpdateChapterRequestMutation();

  const defaultValues =
    chapterRequest === null
      ? {
          parentName: organization?.name,
          name: '',
          mailingAddress: undefined,
          emailAddress: '',
          ein: organization?.ein,
          addAdditionalAdmin: false,
          adminEmail: '',
          adminFirstName: '',
          adminLastName: '',
        }
      : {
          parentName: organization?.name,
          name: chapterRequest.nonprofitName,
          mailingAddress: {
            ...chapterRequest.locationObject,
            __typename: undefined,
            geo: { ...chapterRequest.locationObject.geo, __typename: undefined },
          },
          emailAddress: chapterRequest.email,
          ein: chapterRequest.identifiers[0].id,
          addAdditionalAdmin: chapterRequest.users.length > 0,
          adminEmail: chapterRequest.users[0]?.email || '',
          adminFirstName: chapterRequest.users[0]?.fullName.first || '',
          adminLastName: chapterRequest.users[0]?.fullName.last || '',
        };

  const {
    control,
    getValues,
    watch,
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, touchedFields },
  } = useForm<ChapterFormData>({
    defaultValues,
    mode: 'onChange',
  });

  if (!organization || !user) {
    return null;
  }

  const onSubmit = reactHookFormHandleSubmit(async () => {
    const formValues = getValues();
    const {
      emailAddress,
      ein,
      mailingAddress,
      name,
      addAdditionalAdmin,
      adminEmail,
      adminFirstName,
      adminLastName,
    } = formValues;

    const users = [];

    if (addAdditionalAdmin) {
      users.push({
        email: adminEmail,
        roles: [RoleCode.Administrator, RoleCode.Owner, RoleCode.Verified],
        fullName: {
          first: adminFirstName,
          last: adminLastName,
        },
      });
    }

    if (chapterRequest !== null) {
      await updateChapterRequestMutation({
        variables: {
          id: chapterRequest.id,
          input: {
            email: emailAddress,
            identifiers: [{ type: NonprofitIdentifierType.Ein, id: ein }],
            locationObject: mailingAddress,
            nonprofitName: name,
            users,
          },
        },
        onCompleted: () => {
          onClose();
        },
      });
    } else {
      await createChapterRequestMutation({
        variables: {
          input: {
            email: emailAddress,
            identifiers: [{ type: NonprofitIdentifierType.Ein, id: ein }],
            locationObject: mailingAddress,
            nonprofitName: name,
            parentNonprofitId: organization.nonprofitId,
            users,
          },
        },
        onCompleted: () => {
          onClose();
          history.push('/chapters/pending');
        },
      });
    }
  });

  const formTitle = chapterRequest === null ? 'Add a chapter' : 'Edit chapter';
  const submitButtonText = chapterRequest === null ? 'Add a chapter' : 'Save changes';

  return (
    <Modal open={modalOpen} fullWidth onClose={onClose}>
      <ModalContents
        onClickClose={onClose}
        header={<DeedHeartHands />}
        headerStyles={{ paddingTop: 16 }}
        body={
          <Box mb={4}>
            <ModalTitle>{formTitle}</ModalTitle>
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <Controller
                name="parentName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Parent organization"
                    fullWidth
                    required
                    disabled
                    error={Boolean(errors.parentName)}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'This field is required',
                  maxLength: { value: 100, message: 'Please use less than 100 characters' },
                  validate: (value) =>
                    value.trim().toLowerCase() !== organization.name.trim().toLowerCase(),
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      label="Chapter name"
                      fullWidth
                      required
                      error={Boolean(errors.name)}
                      helperText={touchedFields.name && errors.name?.message}
                    />
                    <Alert severity="info" style={{ marginTop: '8px' }}>
                      We recommend prefixing the chapter name with the parent name. For example - If
                      Deed was a nonprofit organization with a chapter based in Charleston, SC, the
                      chapter name would be Deed - Charleston in our portal. This helps standardize
                      chapter names and make them easily recognizable to potential donors.
                    </Alert>
                  </>
                )}
              />

              <Controller
                name="mailingAddress"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isLocationObject(),
                }}
                render={({ field }) => (
                  <FormLocationObjectField
                    input={field}
                    meta={{
                      error: Boolean(errors.mailingAddress),
                      touched: Boolean(touchedFields.mailingAddress),
                    }}
                    label="Mailing address"
                    sx={{ textTransform: 'uppercase' }}
                    fullWidth
                    afterSelect={() => {}}
                    helperText={touchedFields.mailingAddress && errors.mailingAddress?.message}
                  />
                )}
              />

              <Controller
                name="emailAddress"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isEmail(),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter Email address"
                    fullWidth
                    required
                    error={Boolean(errors.emailAddress)}
                    helperText={touchedFields.emailAddress && errors.emailAddress?.message}
                  />
                )}
              />

              <Controller
                name="ein"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Chapter EIN / tax ID" fullWidth required disabled />
                )}
              />

              <Box>
                <Alert severity="info" style={{ marginBottom: '16px' }}>
                  All owners for the parent organization will automatically be added to the chapter.
                </Alert>

                <Controller
                  name="addAdditionalAdmin"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => {
                            onChange(event.target.checked);
                          }}
                        />
                      }
                      label="Add an additional user as an owner"
                    />
                  )}
                />
              </Box>

              {watch('addAdditionalAdmin') && (
                <>
                  <Controller
                    name="adminEmail"
                    control={control}
                    rules={{
                      required: 'This field is required',
                      validate: isEmail(),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin email address"
                        fullWidth
                        required
                        error={Boolean(errors.adminEmail)}
                        helperText={
                          (touchedFields.emailAddress && errors.emailAddress?.message) ||
                          'Tip: By default, all active Owners for the parent organization will automatically be added as Owners for any chapters created. (if you share an EIN)'
                        }
                      />
                    )}
                  />
                  <Controller
                    name="adminFirstName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin first name"
                        fullWidth
                        required
                        error={Boolean(errors.adminFirstName)}
                        helperText={touchedFields.adminFirstName && errors.adminFirstName?.message}
                      />
                    )}
                  />

                  <Controller
                    name="adminLastName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin last name"
                        fullWidth
                        required
                        error={Boolean(errors.adminLastName)}
                        helperText={touchedFields.adminLastName && errors.adminLastName?.message}
                      />
                    )}
                  />
                </>
              )}
            </form>

            {(creatingError || updatingError) && (
              <Alert severity="error" style={{ marginTop: '20px' }}>
                Submission failed, please try again. If the error persists, please contact support.
              </Alert>
            )}
          </Box>
        }
        actions={
          <Button
            color="primary"
            type="button"
            variant="contained"
            disabled={creating || updating}
            onClick={onSubmit}
          >
            {submitButtonText}
          </Button>
        }
      />
    </Modal>
  );
};

export default ChapterRequestModal;
